import React, { FC } from 'react';
import cn from 'classnames';

import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';
import { createUrlWithSourceQueryParams } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';

import type { FilterLinksProps } from '../types';
import BorderContainer from '../../BorderContainer';
import { useAppContext } from '../../../context/AppContext';
import { CATALOG_AMPLITUDE_PAGE_NAME } from '../../../constants';
import ButtonLinkIcon from '../ButtonLinkIcon';
import { LinkType, PAYG_FILTER_LINKS, PREMIUM_FILTER_LINKS } from '../contants';

const FilterLinks: FC<FilterLinksProps> = ({ scrollTop }) => {
  const { orderBy, filters, orderByActions, filterActions } = useAppContext();
  const { me, loading: loadingMe } = useMe();
  const { t } = useTranslation();

  const onSelectOrderOption = (option: LinkType) => {
    scrollTop?.();

    filterActions.clearAllFilters();

    // @ts-expect-error
    orderByActions.selectOrderBy(option);
  };

  const renderCommonOptions = (items_: ReadonlyArray<LinkType>) => {
    const hasCategorySelected =
      !!filters.categorySelected?.itemId &&
      filters.categorySelected?.itemId !== 'null';

    return items_.map((item, index) => {
      const isSelected =
        !hasCategorySelected && orderBy.itemSelected.value === item.value;
      const isLastItem = index === items_.length - 1;

      return (
        <li
          key={item.value}
          className={cn({
            'mb-16': !isLastItem,
          })}
        >
          <button
            type="button"
            onClick={() => onSelectOrderOption(item)}
            className={cn(
              'transition-all duration-500 ease-in-out hover:text-primary-main tw-font-semibold',
              isSelected
                ? 'text-primary-main border-solid border-l-4 border-primary-main pl-4'
                : 'border-solid border-l-4 border-transparent hover:border-gray-lighter pl-4',
            )}
          >
            {t(item.label, { defaultValue: item.label })}
          </button>
        </li>
      );
    });
  };

  const renderPaygOptions = () => {
    const premiumLink = createUrlWithSourceQueryParams({
      url: '/premium/',
      source: CATALOG_AMPLITUDE_PAGE_NAME,
      sourceDetail: 'FilterLinks',
    });

    return (
      <>
        <BorderContainer hideBorderTop noPaddingTop className="tw-font-pop">
          <ul className="text-16 text-base-main" id="filter-desktop">
            {renderCommonOptions(PAYG_FILTER_LINKS)}
          </ul>
        </BorderContainer>
        <BorderContainer hideBorderTop className="tw-font-pop">
          <ul className="text-16 text-base-main">
            <li>
              <ButtonLinkIcon
                label={t('CATALOG_BE_PREMIUM')}
                iconType="star"
                noMarginBottom
                href={premiumLink}
                blank
              />
            </li>
          </ul>
        </BorderContainer>
      </>
    );
  };

  const renderPremiumOptions = () => {
    return (
      <BorderContainer hideBorderTop noPaddingTop className="tw-font-pop">
        {renderCommonOptions(PREMIUM_FILTER_LINKS)}
      </BorderContainer>
    );
  };

  const renderSkeleton = () => {
    return (
      <div className="w-full mb-8 h-96">
        <ul>
          <li className="w-6/12 h-24 mb-16 bg-gray-lighter" />
          <li className="w-4/12 h-24 mb-16 bg-gray-lighter" />
          <li className="w-7/12 h-24 bg-gray-lighter" />
        </ul>
      </div>
    );
  };

  const renderContent = () => {
    if (loadingMe) return renderSkeleton();

    if (me?.isPremiumMember) {
      return renderPremiumOptions();
    }

    // Users: not premium, payg, or free.
    return renderPaygOptions();
  };

  return renderContent();
};

export default FilterLinks;
